import {
  DndContext,
  useDraggable,
  useDroppable,
  type DragEndEvent,
} from "@dnd-kit/core";
import { useMemo, useState, type CSSProperties } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { deletionReferences } from "@wedding/api-client";
import type { EntityRecord } from "@wedding/schemas";
import { Button, Card, Skeleton } from "@wedding/ui";
import {
  AlertTriangle,
  Check,
  GripVertical,
  Lock,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { api } from "../api";
import { ModuleTodoSection } from "../components/ModuleTodoSection";

type TableDraft = {
  tableNumber: string;
  tableName: string;
  area: string;
  capacity: number;
  captain: string;
  locked: boolean;
  notes: string;
};

type SeatSlot = {
  key: string;
  label: string;
  kind: "host" | "honored" | "regular";
  x: number;
  y: number;
};

const emptyDraft = (number: number): TableDraft => ({
  tableNumber: String(number),
  tableName: `第 ${number} 桌`,
  area: "",
  capacity: 10,
  captain: "",
  locked: false,
  notes: "",
});

function guestIds(table: EntityRecord) {
  return Array.isArray(table.data.guestIds)
    ? table.data.guestIds.filter(
        (guestId): guestId is string => typeof guestId === "string",
      )
    : [];
}

function seatSlots(capacity: number): SeatSlot[] {
  const count = Math.max(1, Math.min(20, Math.round(capacity || 10)));
  const deputyHost = Math.floor(count / 2);
  const labels = new Map<number, Pick<SeatSlot, "label" | "kind">>();
  labels.set(0, { label: "主陪", kind: "host" });
  if (count > 1) labels.set(1, { label: "主宾", kind: "honored" });
  if (count > 2) labels.set(count - 1, { label: "副宾", kind: "honored" });
  if (count > 3) labels.set(deputyHost, { label: "副陪", kind: "host" });
  if (count > 6) {
    labels.set((deputyHost + 1) % count, {
      label: "三宾",
      kind: "honored",
    });
    labels.set((deputyHost - 1 + count) % count, {
      label: "四宾",
      kind: "honored",
    });
  }
  return Array.from({ length: count }, (_, index) => {
    const angle = ((90 - (index * 360) / count) * Math.PI) / 180;
    const role = labels.get(index) ?? {
      label: `${index + 1}号席`,
      kind: "regular" as const,
    };
    return {
      key: `seat-${index}`,
      ...role,
      x: 50 + Math.cos(angle) * 42,
      y: 50 + Math.sin(angle) * 42,
    };
  });
}

function tableAssignments(table: EntityRecord): Record<string, string> {
  const stored = table.data.seatAssignments;
  if (stored && typeof stored === "object" && !Array.isArray(stored))
    return Object.fromEntries(
      Object.entries(stored).filter(
        (entry): entry is [string, string] => typeof entry[1] === "string",
      ),
    );
  const slots = seatSlots(Number(table.data.capacity ?? 10));
  return Object.fromEntries(
    guestIds(table).flatMap((guestId, index) =>
      slots[index] ? [[slots[index]!.key, guestId]] : [],
    ),
  );
}

function seatingPayload(assignments: Record<string, string>) {
  return {
    seatAssignments: assignments,
    guestIds: [...new Set(Object.values(assignments))],
  };
}

function GuestChip({
  guest,
  selected,
  disabled = false,
  onSelect,
}: {
  guest: EntityRecord;
  selected: boolean;
  disabled?: boolean;
  onSelect(guest: EntityRecord): void;
}) {
  const draggable = useDraggable({ id: guest.id, disabled });
  return (
    <div
      ref={draggable.setNodeRef}
      style={{
        transform: draggable.transform
          ? `translate3d(${draggable.transform.x}px,${draggable.transform.y}px,0)`
          : undefined,
      }}
      className={`guest-chip ${selected ? "selected" : ""}`}
    >
      <button
        type="button"
        className="guest-drag-handle"
        {...draggable.listeners}
        {...draggable.attributes}
        disabled={disabled}
        aria-label={`拖拽宾客 ${String(guest.data.name ?? "未命名")}`}
      >
        <GripVertical size={14} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="guest-select-button"
        disabled={disabled}
        onClick={() => onSelect(guest)}
        aria-pressed={selected}
        aria-label={`选择宾客 ${String(guest.data.name ?? "未命名")}`}
      >
        <span>{String(guest.data.name ?? "未命名")}</span>
        {Boolean(guest.data.side) && <small>{String(guest.data.side)}</small>}
        {Number(guest.data.childrenCount) > 0 && <small>含儿童</small>}
      </button>
    </div>
  );
}

function RoundSeat({
  table,
  slot,
  guest,
  selectedGuest,
  onSelectGuest,
  onAssign,
  onRemove,
}: {
  table: EntityRecord;
  slot: SeatSlot;
  guest: EntityRecord | undefined;
  selectedGuest: EntityRecord | undefined;
  onSelectGuest(guest: EntityRecord): void;
  onAssign(table: EntityRecord, seatKey: string): void;
  onRemove(table: EntityRecord, seatKey: string): void;
}) {
  const locked = Boolean(table.data.locked);
  const droppable = useDroppable({
    id: `${table.id}::${slot.key}`,
    disabled: locked,
  });
  const style = {
    "--seat-x": `${slot.x}%`,
    "--seat-y": `${slot.y}%`,
  } as CSSProperties;
  return (
    <div
      ref={droppable.setNodeRef}
      className={`round-seat ${slot.kind} ${droppable.isOver ? "drop-active" : ""} ${guest ? "occupied" : ""}`}
      style={style}
    >
      <span className="seat-role">{slot.label}</span>
      {guest ? (
        <div className="seat-guest">
          <GuestChip
            guest={guest}
            selected={selectedGuest?.id === guest.id}
            disabled={locked}
            onSelect={onSelectGuest}
          />
          {!locked && (
            <button
              type="button"
              className="remove-seat"
              onClick={() => onRemove(table, slot.key)}
              aria-label={`将 ${String(guest.data.name)} 移出 ${slot.label}`}
            >
              <X size={12} />
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="empty-seat"
          disabled={!selectedGuest || locked}
          onClick={() => onAssign(table, slot.key)}
          aria-label={`安排宾客到 ${String(table.data.tableName)} ${slot.label}`}
        >
          {locked ? "已锁定" : selectedGuest ? "安排到此席" : "空位"}
        </button>
      )}
    </div>
  );
}

function RoundTable({
  table,
  guests,
  selectedGuest,
  onSelectGuest,
  onAssign,
  onRemove,
  onEdit,
}: {
  table: EntityRecord;
  guests: EntityRecord[];
  selectedGuest: EntityRecord | undefined;
  onSelectGuest(guest: EntityRecord): void;
  onAssign(table: EntityRecord, seatKey: string): void;
  onRemove(table: EntityRecord, seatKey: string): void;
  onEdit(table: EntityRecord): void;
}) {
  const locked = Boolean(table.data.locked);
  const capacity = Number(table.data.capacity ?? 10);
  const assignments = tableAssignments(table);
  const slots = seatSlots(capacity);
  const guestsById = new Map(guests.map((guest) => [guest.id, guest]));
  const seated = Object.values(assignments).filter((id) => guestsById.has(id));
  const over = seated.length > capacity;
  return (
    <Card className={`seating-table round-table-card ${over ? "over" : ""}`}>
      <header>
        <div>
          <span className="table-number">
            {String(table.data.tableNumber ?? "—")}
          </span>
          <div>
            <strong>{String(table.data.tableName ?? "未命名桌")}</strong>
            <small>{String(table.data.area ?? "未设置区域")}</small>
          </div>
        </div>
        <div className="table-meta-actions">
          {locked ? (
            <Lock size={16} aria-label="已锁定" />
          ) : (
            <span className={over ? "danger-text" : ""}>
              {seated.length} / {capacity}
            </span>
          )}
          <button
            type="button"
            className="icon-button"
            onClick={() => onEdit(table)}
            aria-label={`编辑 ${String(table.data.tableName ?? "桌位")}`}
          >
            <Pencil size={15} />
          </button>
        </div>
      </header>
      <div className="round-table-map">
        <div className="banquet-entrance">宴会厅入口 / 上菜方向</div>
        <div className="round-table-center">
          <strong>{String(table.data.tableName ?? "圆桌")}</strong>
          <span>主陪面向入口</span>
          <small>先选宾客，再点具体席位</small>
        </div>
        {slots.map((slot) => (
          <RoundSeat
            key={slot.key}
            table={table}
            slot={slot}
            guest={guestsById.get(assignments[slot.key] ?? "")}
            selectedGuest={selectedGuest}
            onSelectGuest={onSelectGuest}
            onAssign={onAssign}
            onRemove={onRemove}
          />
        ))}
      </div>
      <div className="shandong-seat-legend">
        <span>
          <i className="host" />
          主陪 / 副陪
        </span>
        <span>
          <i className="honored" />
          主宾 / 副宾 / 三宾 / 四宾
        </span>
        <span>
          <i />
          其他宾客
        </span>
      </div>
      {over && (
        <footer>
          <AlertTriangle size={15} /> 超出容量 {seated.length - capacity} 人
        </footer>
      )}
    </Card>
  );
}

export function SeatingPage() {
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string;
  };
  const queryClient = useQueryClient();
  const [view, setView] = useState<"records" | "tasks" | "library">("records");
  const [search, setSearch] = useState("");
  const [side, setSide] = useState("");
  const [attendance, setAttendance] = useState("");
  const [selectedGuest, setSelectedGuest] = useState<EntityRecord>();
  const [editingTable, setEditingTable] = useState<EntityRecord>();
  const [tableOpen, setTableOpen] = useState(false);
  const [draft, setDraft] = useState<TableDraft>(() => emptyDraft(1));
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [feedback, setFeedback] = useState("");

  const guests = useQuery({
    queryKey: [workspaceId, "guests"],
    queryFn: () =>
      api.list(workspaceId, "guests").then((result) => result.data),
  });
  const tables = useQuery({
    queryKey: [workspaceId, "tables"],
    queryFn: () =>
      api.list(workspaceId, "tables").then((result) => result.data),
  });
  const sortedTables = useMemo(
    () =>
      [...(tables.data ?? [])].sort((a, b) =>
        String(a.data.tableNumber ?? "").localeCompare(
          String(b.data.tableNumber ?? ""),
          "zh-CN",
          { numeric: true },
        ),
      ),
    [tables.data],
  );

  const saveTable = useMutation({
    mutationFn: async () => {
      if (!editingTable)
        return api.create(workspaceId, "tables", {
          ...draft,
          guestIds: [],
          seatAssignments: {},
        });
      const assignments = tableAssignments(editingTable);
      const allowed = new Set(
        seatSlots(draft.capacity).map((slot) => slot.key),
      );
      if (Object.keys(assignments).some((key) => !allowed.has(key)))
        throw new Error("缩小容量前，请先移出将被取消席位上的宾客");
      return api.update(
        workspaceId,
        "tables",
        editingTable.id,
        { ...draft, ...seatingPayload(assignments) },
        editingTable.version,
      );
    },
    onSuccess: async () => {
      setTableOpen(false);
      setEditingTable(undefined);
      setDeleteConfirmation("");
      setFeedback("桌位已保存");
      await queryClient.invalidateQueries({
        queryKey: [workspaceId, "tables"],
      });
    },
  });
  const removeTable = useMutation({
    mutationFn: ({
      table,
      cascade,
    }: {
      table: EntityRecord;
      cascade: boolean;
    }) => api.remove(workspaceId, "tables", table.id, table.version, cascade),
    onSuccess: async (_, { cascade }) => {
      setTableOpen(false);
      setEditingTable(undefined);
      setDeleteConfirmation("");
      setFeedback("桌位已删除，原有宾客已回到待安排列表");
      await queryClient.invalidateQueries({
        queryKey: cascade ? [workspaceId] : [workspaceId, "tables"],
      });
    },
  });
  const deleteReferences = deletionReferences(removeTable.error);

  const moveGuest = async (
    guestId: string,
    target: EntityRecord,
    seatKey: string,
  ) => {
    if (target.data.locked) return;
    const targetAssignments = tableAssignments(target);
    if (targetAssignments[seatKey] && targetAssignments[seatKey] !== guestId) {
      setFeedback("该席位已有宾客，请先移出原宾客");
      return;
    }
    try {
      const changed = (tables.data ?? []).flatMap((table) => {
        const current = tableAssignments(table);
        const next = Object.fromEntries(
          Object.entries(current).filter(([, id]) => id !== guestId),
        );
        if (table.id === target.id) next[seatKey] = guestId;
        return JSON.stringify(current) === JSON.stringify(next)
          ? []
          : [{ table, assignments: next }];
      });
      for (const change of changed.filter(
        (item) => item.table.id !== target.id,
      ))
        await api.update(
          workspaceId,
          "tables",
          change.table.id,
          seatingPayload(change.assignments),
          change.table.version,
        );
      const targetChange = changed.find((item) => item.table.id === target.id);
      if (targetChange)
        await api.update(
          workspaceId,
          "tables",
          target.id,
          seatingPayload(targetChange.assignments),
          target.version,
        );
      setSelectedGuest(undefined);
      setFeedback(
        `已安排到 ${String(target.data.tableName)}的${seatSlots(Number(target.data.capacity ?? 10)).find((slot) => slot.key === seatKey)?.label ?? "指定席位"}`,
      );
      await queryClient.invalidateQueries({
        queryKey: [workspaceId, "tables"],
      });
    } catch (error) {
      setFeedback(
        error instanceof Error ? error.message : "座位更新失败，请重试",
      );
    }
  };

  const removeFromSeat = async (table: EntityRecord, seatKey: string) => {
    const assignments = tableAssignments(table);
    delete assignments[seatKey];
    try {
      await api.update(
        workspaceId,
        "tables",
        table.id,
        seatingPayload(assignments),
        table.version,
      );
      setFeedback("宾客已移回待安排列表");
      await queryClient.invalidateQueries({
        queryKey: [workspaceId, "tables"],
      });
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "移出席位失败");
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    if (!event.over) return;
    const [tableId, seatKey] = String(event.over.id).split("::");
    const target = tables.data?.find((table) => table.id === tableId);
    if (target && seatKey)
      void moveGuest(String(event.active.id), target, seatKey);
  };

  if (guests.isLoading || tables.isLoading)
    return (
      <div className="page">
        <Skeleton lines={10} />
      </div>
    );

  const assignedCounts = new Map<string, number>();
  for (const table of tables.data ?? [])
    for (const id of guestIds(table))
      assignedCounts.set(id, (assignedCounts.get(id) ?? 0) + 1);
  const allAssigned = new Set(assignedCounts.keys());
  const eligibleGuests = (guests.data ?? []).filter(
    (guest) => guest.data.attendance !== "不出席",
  );
  const unassigned = eligibleGuests.filter(
    (guest) => !allAssigned.has(guest.id),
  );
  const sides = [
    ...new Set(
      eligibleGuests
        .map((guest) => String(guest.data.side ?? ""))
        .filter(Boolean),
    ),
  ];
  const filteredGuests = unassigned.filter((guest) => {
    const query = search.trim().toLowerCase();
    const matchesSearch =
      !query ||
      [guest.data.name, guest.data.phone, guest.data.relationship].some(
        (value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(query),
      );
    return (
      matchesSearch &&
      (!side || guest.data.side === side) &&
      (!attendance || guest.data.attendance === attendance)
    );
  });
  const issues: string[] = [];
  for (const table of tables.data ?? []) {
    const count = guestIds(table).length;
    if (count > Number(table.data.capacity ?? 10))
      issues.push(`${String(table.data.tableName)} 超出容量`);
    const absent = guestIds(table).filter(
      (id) =>
        guests.data?.find((guest) => guest.id === id)?.data.attendance ===
        "不出席",
    );
    if (absent.length)
      issues.push(
        `${String(table.data.tableName)} 含 ${absent.length} 位不出席宾客`,
      );
  }
  const duplicateCount = [...assignedCounts.values()].filter(
    (count) => count > 1,
  ).length;
  if (duplicateCount) issues.push(`${duplicateCount} 位宾客被重复排座`);

  const openNewTable = () => {
    const numbers = sortedTables
      .map((table) => Number(table.data.tableNumber))
      .filter(Number.isFinite);
    const nextNumber = numbers.length ? Math.max(...numbers) + 1 : 1;
    setEditingTable(undefined);
    removeTable.reset();
    setDraft(emptyDraft(nextNumber));
    setDeleteConfirmation("");
    setTableOpen(true);
  };
  const openEditTable = (table: EntityRecord) => {
    setEditingTable(table);
    removeTable.reset();
    setDraft({
      tableNumber: String(table.data.tableNumber ?? ""),
      tableName: String(table.data.tableName ?? ""),
      area: String(table.data.area ?? ""),
      capacity: Number(table.data.capacity ?? 10),
      captain: String(table.data.captain ?? ""),
      locked: Boolean(table.data.locked),
      notes: String(table.data.notes ?? ""),
    });
    setDeleteConfirmation("");
    setTableOpen(true);
  };

  return (
    <DndContext onDragEnd={onDragEnd}>
      <div className="page">
        <header className="page-header">
          <div>
            <span className="eyebrow">宾客与座位</span>
            <h1>山东婚宴圆桌排座</h1>
            <p>
              主陪面向入口，主宾在主陪右手位、副宾在左手位，副陪与主陪相对。
            </p>
          </div>
          {view === "records" && (
            <Button onClick={openNewTable}>
              <Plus size={17} /> 新增圆桌
            </Button>
          )}
        </header>
        <div
          className="workspace-view-tabs"
          role="tablist"
          aria-label="座位安排工作区"
        >
          <button
            className={view === "records" ? "active" : ""}
            onClick={() => setView("records")}
            role="tab"
            aria-selected={view === "records"}
          >
            圆桌座位图
          </button>
          <button
            className={view === "tasks" ? "active" : ""}
            onClick={() => setView("tasks")}
            role="tab"
            aria-selected={view === "tasks"}
          >
            筹备事项
          </button>
          <button
            className={view === "library" ? "active" : ""}
            onClick={() => setView("library")}
            role="tab"
            aria-selected={view === "library"}
          >
            事项库
          </button>
        </div>
        {view === "records" ? (
          <>
            {feedback && (
              <div className="page-notice" role="status">
                {feedback}
              </div>
            )}
            {issues.length > 0 && (
              <div className="alert-banner">
                <AlertTriangle />
                <div>
                  <strong>发现 {issues.length} 个排座问题</strong>
                  <span>{issues.join("；")}</span>
                </div>
              </div>
            )}
            <div className="seating-layout shandong-seating-layout">
              <aside className="guest-pool">
                <header>
                  <Users size={18} />
                  <div>
                    <strong>待安排宾客</strong>
                    <small>
                      {filteredGuests.length} / {unassigned.length} 人
                    </small>
                  </div>
                </header>
                <div className="guest-pool-filters">
                  <label className="seat-search">
                    <Search size={14} />
                    <input
                      aria-label="搜索待安排宾客"
                      placeholder="姓名、电话或关系"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </label>
                  <select
                    aria-label="按归属方筛选"
                    value={side}
                    onChange={(event) => setSide(event.target.value)}
                  >
                    <option value="">全部归属方</option>
                    {sides.map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                  <select
                    aria-label="按出席状态筛选"
                    value={attendance}
                    onChange={(event) => setAttendance(event.target.value)}
                  >
                    <option value="">全部状态</option>
                    <option>出席</option>
                    <option>待确认</option>
                  </select>
                </div>
                <div className="guest-pool-list">
                  {filteredGuests.map((guest) => (
                    <GuestChip
                      key={guest.id}
                      guest={guest}
                      selected={selectedGuest?.id === guest.id}
                      onSelect={setSelectedGuest}
                    />
                  ))}
                  {!filteredGuests.length && (
                    <p>
                      {unassigned.length
                        ? "没有符合筛选条件的宾客"
                        : "所有可出席宾客均已排座"}
                    </p>
                  )}
                </div>
              </aside>
              <section
                className="tables-grid round-tables-grid"
                aria-label="圆桌座位列表"
              >
                {sortedTables.map((table) => (
                  <RoundTable
                    key={table.id}
                    table={table}
                    guests={guests.data ?? []}
                    selectedGuest={selectedGuest}
                    onSelectGuest={setSelectedGuest}
                    onAssign={(target, seatKey) =>
                      selectedGuest &&
                      void moveGuest(selectedGuest.id, target, seatKey)
                    }
                    onRemove={(target, seatKey) =>
                      void removeFromSeat(target, seatKey)
                    }
                    onEdit={openEditTable}
                  />
                ))}
                {!sortedTables.length && (
                  <Card className="seating-empty">
                    <h2>还没有圆桌</h2>
                    <p>先新增第一桌，再按主陪、主宾等席位安排宾客。</p>
                    <Button onClick={openNewTable}>
                      <Plus size={16} /> 新增第一桌
                    </Button>
                  </Card>
                )}
              </section>
            </div>
          </>
        ) : (
          <ModuleTodoSection
            workspaceId={workspaceId}
            moduleType="tables"
            moduleLabel="座位安排"
            view={view === "library" ? "library" : "current"}
          />
        )}
      </div>

      {tableOpen && (
        <div className="drawer-backdrop">
          <aside
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label={editingTable ? "编辑桌位" : "新增桌位"}
          >
            <header>
              <div>
                <span className="eyebrow">山东圆桌</span>
                <h2>{editingTable ? "编辑圆桌" : "新增圆桌"}</h2>
              </div>
              <button
                className="icon-button"
                aria-label="关闭"
                onClick={() => setTableOpen(false)}
              >
                <X />
              </button>
            </header>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                saveTable.mutate();
              }}
            >
              <div className="form-grid">
                <label>
                  <span>桌号</span>
                  <input
                    required
                    value={draft.tableNumber}
                    onChange={(event) =>
                      setDraft({ ...draft, tableNumber: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>桌名</span>
                  <input
                    required
                    value={draft.tableName}
                    onChange={(event) =>
                      setDraft({ ...draft, tableName: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>区域</span>
                  <input
                    value={draft.area}
                    onChange={(event) =>
                      setDraft({ ...draft, area: event.target.value })
                    }
                    placeholder="例如：主宴会厅左侧"
                  />
                </label>
                <label>
                  <span>容量</span>
                  <input
                    required
                    type="number"
                    min="6"
                    max="12"
                    value={draft.capacity}
                    onChange={(event) =>
                      setDraft({
                        ...draft,
                        capacity: Number(event.target.value),
                      })
                    }
                  />
                </label>
                <label>
                  <span>排座负责人</span>
                  <input
                    value={draft.captain}
                    onChange={(event) =>
                      setDraft({ ...draft, captain: event.target.value })
                    }
                  />
                </label>
                <label className="inline-check">
                  <input
                    type="checkbox"
                    checked={draft.locked}
                    onChange={(event) =>
                      setDraft({ ...draft, locked: event.target.checked })
                    }
                  />{" "}
                  锁定圆桌，防止误排
                </label>
                <label className="span-2">
                  <span>备注</span>
                  <textarea
                    rows={3}
                    value={draft.notes}
                    onChange={(event) =>
                      setDraft({ ...draft, notes: event.target.value })
                    }
                  />
                </label>
              </div>
              {saveTable.error && (
                <p className="form-error">{saveTable.error.message}</p>
              )}
              {editingTable && (
                <div className="table-delete-section">
                  {deleteReferences.length > 0 && (
                    <div className="dependency-warning" role="alert">
                      <strong>
                        这张圆桌还出现在以下 {deleteReferences.length}{" "}
                        条数据中：
                      </strong>
                      <ul>
                        {deleteReferences.map((reference) => (
                          <li
                            key={`${reference.entityType}:${reference.entityId}`}
                          >
                            <span>{reference.moduleLabel}</span>
                            <b>「{reference.recordLabel}」</b>
                            <small>{reference.relationLabel}</small>
                          </li>
                        ))}
                      </ul>
                      <p>再次确认后，上述关联数据也会一并删除。</p>
                    </div>
                  )}
                  {removeTable.error && !deleteReferences.length && (
                    <p className="form-error">{removeTable.error.message}</p>
                  )}
                  <label>
                    <span>
                      删除圆桌时，请输入桌名「
                      {String(editingTable.data.tableName)}」
                    </span>
                    <input
                      value={deleteConfirmation}
                      onChange={(event) =>
                        setDeleteConfirmation(event.target.value)
                      }
                    />
                  </label>
                  <Button
                    type="button"
                    className="danger"
                    disabled={
                      deleteConfirmation !==
                        String(editingTable.data.tableName) ||
                      removeTable.isPending
                    }
                    onClick={() =>
                      removeTable.mutate({
                        table: editingTable,
                        cascade: deleteReferences.length > 0,
                      })
                    }
                  >
                    <Trash2 size={15} />{" "}
                    {removeTable.isPending
                      ? "删除中…"
                      : deleteReferences.length
                        ? "确认并一并删除"
                        : "删除圆桌"}
                  </Button>
                </div>
              )}
              <footer>
                <Button
                  type="button"
                  className="secondary"
                  onClick={() => setTableOpen(false)}
                >
                  取消
                </Button>
                <Button type="submit" disabled={saveTable.isPending}>
                  {saveTable.isPending ? "保存中…" : "保存圆桌"}
                </Button>
              </footer>
            </form>
          </aside>
        </div>
      )}
    </DndContext>
  );
}
